import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// import { signInForNaver } from "#/auth/signIn";
import { NAVER_AUTH_URL } from "#/auth/signIn";
import { Splash } from "@/Splash";
import { OAuthErrorView } from "@/common/OAuthErrorView";
import { useEffect, useState, useCallback } from "react";
import { useCookies } from "react-cookie";
import { useStorage } from "^/common/useStorage";
import { AppType, SignType } from "#/unique/domain";
import {
  businessAppLogin,
  useBusinessLogin,
  userAppLogin,
  useUserLogin,
} from "~/services/auth";
import { SHA256 } from "crypto-js";

export const Route = createFileRoute("/oauth/naver")({
  component: RouteComponent,
});

function RouteComponent() {
  const [, setCookie] = useCookies(["email"]);
  const [type] = useStorage<AppType>("session-app-type");
  const [, setSns] = useStorage<SignType>("local-sign-sns-type");
  const [, setFcm] = useStorage<string>("session-fcm");
  const [, setSnsId] = useStorage<string>("local-sns-id");
  const [shouldCheckBusinessSign, setShouldCheckBusinessSign] = useState(false);
  const [shouldCheckCustomerSign, setShouldCheckCustomerSign] = useState(false);

  const params = new URL(document.URL).searchParams;
  const code = params.get("code");
  const state = params.get("state");
  const errorParam = params.get("error");
  console.log("[OAUTH-NAVER] 콜백 진입", {
    url: document.URL,
    code: code ? `${code.substring(0, 20)}...` : "❌ NO CODE",
    state,
    error: errorParam || null,
    allParams: Object.fromEntries(params.entries()),
  });

  const { data, error: queryError } = useQuery({
    queryKey: ["/oauth/naver"],
    enabled: !!code && !errorParam,
    retry: false,
    queryFn: async () => {
      const apiUrl = import.meta.env.VITE_API_BASE_URL + "/oauth/naver";
      console.log("[OAUTH-NAVER] 백엔드 토큰 교환 시작", { apiUrl, code: !!code, state });
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          state,
          // 운영은 사업자앱/보호자앱이 서로 다른 네이버 앱(client_id)을 쓰므로 백엔드가 맞는 자격증명을 고르도록 알려준다
          appType: import.meta.env.VITE_APP_TYPE === "business" ? "BUSINESS" : "CUSTOMER",
        }),
      });
      console.log("[OAUTH-NAVER] 백엔드 응답", { status: response.status, ok: response.ok });
      if (!response.ok) {
        console.error("[OAUTH-NAVER] 백엔드 응답 실패", { status: response.status });
        throw new Error("네이버 로그인 API 호출 실패");
      }

      const json = await response.json();
      // 백엔드는 네이버 토큰 교환 실패를 HTTP 200 + statusCode 4000 으로 돌려준다 → 실패로 취급
      if (json?.statusCode !== 200 || !json?.data) {
        console.error("[OAUTH-NAVER] 토큰 교환 실패 응답", json);
        throw new Error(json?.message || "네이버 로그인 API 호출 실패");
      }
      return json;
    },
    select: (res) => {
      console.log("[OAUTH-NAVER] 토큰 교환 응답 raw", res);
      return res.data;
    },
  });
  if (queryError) console.error("[OAUTH-NAVER] 토큰 교환 실패", queryError);
  if (data) console.log("[OAUTH-NAVER] 사용자 정보 받음", data);
  const { history } = useRouter();
  // useBusinessLogin을 컴포넌트 레벨에서 호출
  const {
    data: businessLoginData,
    error: businessError,
    isSuccess: isBusinessSuccess,
  } = useBusinessLogin(
    {
      snsId: data?.naverId || "",
      cdSnsType: "SNS_TYPE_NAVER",
    },
    shouldCheckBusinessSign
  );

  // useUserLogin을 컴포넌트 레벨에서 호출
  const {
    data: userLoginData,
    error: userError,
    isSuccess: isUserSuccess,
  } = useUserLogin(
    {
      snsId: data?.naverId || "",
      cdSnsType: "SNS_TYPE_NAVER",
    },
    shouldCheckCustomerSign
  );

  useEffect(() => {
    if (!data) return;

    // 앱 환경 감지 (MT_I 또는 MT_A가 포함된 UserAgent)
    const userAgent = navigator.userAgent;
    const isApp = /MT_I|MT_A/i.test(userAgent);
    const key = "SNS_TYPE_NAVER" + data.naverId;
    const hash = SHA256(key).toString();

    // 앱 환경에서만 appLogin 호출
    if (/MT_A/i.test(userAgent)) {
      window.mung?.appLogin(hash);
    }
    if (/MT_I/i.test(userAgent)) {
      const array = ["appLogin", hash];
      window.webkit?.messageHandlers?.mung?.postMessage(array);
    }

    const fnAppLogin = async (fcm: string) => {
      try {
        const appLoginData = {
          cdSnsType: "SNS_TYPE_NAVER",
          hash: hash,
          fcmToken: fcm,
          cdMobileType: /MT_A/i.test(userAgent) ? "ANDROID" : "IOS",
        };
        if (import.meta.env.VITE_APP_TYPE === "business") {
          const loginResponse = await businessAppLogin(appLoginData);
          console.log("앱 로그인 성공:", loginResponse);
          if (loginResponse?.statusCode === 200) {
            history.push("/business/layout/home");
          } else {
            setShouldCheckBusinessSign(type === "business");
            console.log("앱 로그인 실패:", loginResponse?.statusCode);
          }
        } else {
          const loginResponse = await userAppLogin(appLoginData);
          if (loginResponse?.statusCode === 200) {
            history.push("/customer/layout/home");
          } else {
            setShouldCheckCustomerSign(type === "customer");
            console.log("앱 로그인 실패:", loginResponse?.statusCode);
          }
        }
      } catch (error) {
        console.log("앱 로그인 실패:", error);
        console.error("앱 로그인 실패:", error);
      }
    };

    if (isApp) {
      window.callAppLogin = (data: string) => {
        setFcm(data);
        fnAppLogin(data);
      };
    }

    // 웹 환경에서는 바로 로그인 진행
    if (!isApp) {
      setShouldCheckBusinessSign(type === "business");
      setShouldCheckCustomerSign(type === "customer");
    }

    // 백엔드 응답 필드는 naverId (카카오/구글 콜백의 data.id 를 복사한 버그 수정)
    setSnsId(data.naverId);
    setSns("SNS_TYPE_NAVER");
    setCookie("email", data.email, {
      secure: true,
      maxAge: 3600,
    });
  }, [data, setCookie, setSns, setSnsId, setFcm, type, history]);

  useEffect(() => {
    if (type === "business" && isBusinessSuccess) {
      businessLogin();
    } else if (type === "customer" && isUserSuccess) {
      customerLogin();
    }
  }, [isBusinessSuccess, isUserSuccess, type]);

  const businessLogin = useCallback(() => {
    if (isBusinessSuccess && businessLoginData?.statusCode == 201) {
      history.push("/sign-up");
    } else if (isBusinessSuccess && businessLoginData?.statusCode == 200) {
      history.push("/business/layout/home");
    } else {
      history.push("/");
    }
  }, [isBusinessSuccess, businessLoginData, businessError, history]);

  const customerLogin = useCallback(() => {
    if (isUserSuccess && userLoginData?.statusCode == 201) {
      history.push("/sign-up");
    } else if (isUserSuccess && userLoginData?.statusCode == 200) {
      history.push("/customer/layout/home");
    } else {
      history.push("/");
    }
  }, [isUserSuccess, userLoginData, userError, history]);

  // ── 에러 분기
  if (errorParam) {
    return <OAuthErrorView error={errorParam} provider="naver" retryUrl={NAVER_AUTH_URL} />;
  }
  if (!code) {
    return <OAuthErrorView error="no_code" provider="naver" retryUrl={NAVER_AUTH_URL} />;
  }
  if (queryError) {
    return <OAuthErrorView error="token_exchange_failed" provider="naver" retryUrl={NAVER_AUTH_URL} />;
  }

  return <Splash visible />;
}
